import React, {useContext, useEffect, useState} from "react";
import {AccountContext} from "@/context/AccountProvider";
import {updateAccountById} from "@/service/accountApi";
import {t} from "i18next";
import {Modal} from "./Modal";
import {ChevronDownIcon} from "@radix-ui/react-icons";
import {Transition} from "@headlessui/react";
import {countryCodes} from "@/data/CountryCode";
import {toast} from "react-toastify";
import {useAxiosPrivate} from "@/hooks/useAxiosPrivate";

export const EditProfileForm = (props) => {
	const {showEditPersonalProfileForm, setShowEditPersonalProfileForm, accountDetails, setAccountDetails} = props;
	const bloodGroups = ["A +", "A -", "B +", "B -", "O +", "O -", "AB +", "AB -"];

	const axiosPrivate = useAxiosPrivate();
	const {account, setAccount} = useContext(AccountContext);
	const [error, setError] = useState({});

	const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);

	const [isGenderDropdownOpen, setIsGenderDropdownOpen] = useState(false);
	const [isSelectingGender, setIsSelectingGender] = useState(false);

	const [isMaritalStatusDropdownOpen, setIsMaritalStatusDropdownOpen] = useState(false);
	const [isSelectingMaritalStatus, setIsSelectingMaritalStatus] = useState(false);

	const [isSideOfFamilyDropdownOpen, setIsSideOfFamilyDropdownOpen] = useState(false);
	const [isSelectingSideOfFamily, setIsSelectingSideOfFamily] = useState(false);

	const [isBloodGroupDropdownOpen, setIsBloodGroupDropdownOpen] = useState(false);
	const [isSelectingBloodGroup, setIsSelectingBloodGroup] = useState(false);

	// const [filteredFamilyTreeNames, setFilteredFamilyTreeNames] = useState(familyTreeNames);
	// const [isFamilyTreeNameDropdownOpen, setIsFamilyTreeNameDropdownOpen] = useState(false);

	const [isCountryCodeDropdownOpen, setIsCountryCodeDropdownOpen] = useState(false);
	const [isSelectingCountryCode, setIsSelectingCountryCode] = useState(false);

	const [isHomePhoneCountryCodeDropdownOpen, setIsHomePhoneCountryCodeDropdownOpen] = useState(false);
	const [isSelectingHomePhoneCountryCode, setIsSelectingHomePhoneCountryCode] = useState(false);

	const [isOfficePhoneCountryCodeDropdownOpen, setIsOfficePhoneCountryCodeDropdownOpen] = useState(false);
	const [isSelectingOfficePhoneCountryCode, setIsSelectingOfficePhoneCountryCode] = useState(false);

	const [filteredCountryDetails, setFilteredCountryDetails] = useState(countryCodes);
	const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
	const [isSelectingCountry, setIsSelectingCountry] = useState(false);

	useEffect(() => {
		console.log("(EditProfileForm) Account: ", accountDetails);
	}, []);

	function extractDate(dateString) {
		if (!dateString) {
			return undefined;
		}
		const dateObject = new Date(dateString);
		const year = dateObject.getUTCFullYear();
		const month = String(dateObject.getUTCMonth() + 1).padStart(2, '0');
		const day = String(dateObject.getUTCDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	const [profile, setProfile] = useState({
		isMember: accountDetails?.isMember,
		membershipNumber: accountDetails?.membershipNumber,
		pageNumber: accountDetails?.pageNumber,
		granth2010PageNumber: accountDetails?.granth2010PageNumber,
		generation: accountDetails?.generation,

		email: accountDetails?.email,

		firstName: accountDetails?.firstName,
		middleName: accountDetails?.middleName,

		firstNameDevanagariScript: accountDetails?.firstNameDevanagariScript,
		middleNameDevanagariScript: accountDetails?.middleNameDevanagariScript,

		deceased: accountDetails?.deceased,

		dateOfBirth: extractDate(accountDetails?.dateOfBirth),
		gender: accountDetails?.gender,
		bloodGroup: accountDetails?.bloodGroup,

		phone: {
			charCode: accountDetails?.phone?.charCode || 'IN',
			dialingCode: accountDetails?.phone?.dialingCode || '+91',
			countryName: accountDetails?.phone?.countryName || 'India',
			number: accountDetails?.phone?.number || '',
		},

		homePhone: {
			charCode: accountDetails?.homePhone?.charCode || 'IN',
			dialingCode: accountDetails?.homePhone?.dialingCode || '+91',
			countryName: accountDetails?.homePhone?.countryName || 'India',
			number: accountDetails?.homePhone?.number || '',
		},

		officePhone: {
			charCode: accountDetails?.officePhone?.charCode || 'IN',
			dialingCode: accountDetails?.officePhone?.dialingCode || '+91',
			countryName: accountDetails?.officePhone?.countryName || 'India',
			number: accountDetails?.officePhone?.number || '',
		},

		maritalStatus: accountDetails?.maritalStatus || 'Single',
		sideOfFamily: accountDetails?.sideOfFamily || '',

		firstNameAfterMarriage: accountDetails?.firstNameAfterMarriage || '',
		spouseNameAfterMarriage: accountDetails?.spouseNameAfterMarriage || '',
		lastNameAfterMarriage: accountDetails?.lastNameAfterMarriage || '',

		firstNameBeforeMarriage: accountDetails?.firstNameBeforeMarriage || '',
		fathersNameBeforeMarriage: accountDetails?.fathersNameBeforeMarriage || '',
		lastNameBeforeMarriage: accountDetails?.lastNameBeforeMarriage || '',

		dateOfMarriage: extractDate(accountDetails?.dateOfMarriage),

		dateOfDeath: extractDate(accountDetails?.dateOfDeath),

		familyTreeName: accountDetails?.familyTreeName || '',

		biography: accountDetails?.biography || '',

		address: {
			addressLine1: accountDetails?.address?.addressLine1 || '',
			pincode: accountDetails?.address?.pincode || '',
			city: accountDetails?.address?.city || '',
			state: accountDetails?.address?.state || '',
			country: accountDetails?.address?.country || '',
			countryCode: accountDetails?.address?.countryCode || '',
		},

		imageUrl: accountDetails?.imageUrl || '',
		imageUrlBg: accountDetails?.imageUrlBg || '',
	});

	function checkAge(dateOfBirth) {
		// Convert the date of birth to a Date object
		const birthDate = new Date(dateOfBirth);

		// Calculate the current age in years
		const today = new Date();
		const age = today.getFullYear() - birthDate.getFullYear();

		// Check if the age is less than 13 or greater than 70
		return (age < 13 || age > 70);
	}

	const handleGenderSelect = (gender) => {
		setIsSelectingGender(false);
		setIsGenderDropdownOpen(false);
		setProfile({
			...profile,
			gender: gender
		});
	}

	const maritalStatusLabel = (maritalStatus) => {
		switch (maritalStatus) {
			case 'Single':
				return t("PROFILE_FORM.MARITAL_STATUS.SINGLE");
			case 'Married':
				return t("PROFILE_FORM.MARITAL_STATUS.MARRIED");
			case 'Widowed':
				return accountDetails.gender === "Male"
					? t("PROFILE_FORM.MARITAL_STATUS.WIDOWER")
					: t("PROFILE_FORM.MARITAL_STATUS.WIDOWED");
			case 'Divorced':
				return t("PROFILE_FORM.MARITAL_STATUS.DIVORCED");
			case 'Minor':
				return t("PROFILE_FORM.MARITAL_STATUS.MINOR");
			default:
				return t("PROFILE_FORM.MARITAL_STATUS.SINGLE");
		}
	}

	const handleMaritalStatusSelect = (maritalStatus) => {
		setIsSelectingMaritalStatus(false);
		setIsMaritalStatusDropdownOpen(false);
		setProfile({
			...profile,
			maritalStatus: maritalStatus
		});
	}

	const handleSideOfFamilySelect = (sideOfFamily) => {
		setIsSelectingSideOfFamily(false);
		setIsSideOfFamilyDropdownOpen(false);
		setProfile({
			...profile,
			sideOfFamily: sideOfFamily
		});
	}

	useEffect(() => {
		if (profile.gender === 'Female') {
			if (profile.sideOfFamily === '') {
				setProfile({
					...profile,
					sideOfFamily: 'MaherVashin'
				});
			}
		}
	}, [profile.gender]);

	const isNumberValid = (phone) => {
		let reg = /^\d+$/;
		return reg.test(phone);
	}

	const validatePhoneNumber = (e) => {
		if ((!isNumberValid(e.target.value)) || e.target.value.length !== 10) {
			if (e.target.name === 'officePhone' || e.target.name === 'homePhone') {
				if (e.target.value === "") {
					setError({
						...error,
						[e.target.name]: ""
					});
				}
			} else {
				if (e.target.value === "") {
					setError({
						...error,
						[e.target.name]: t("PROFILE_FORM.PHONE_NUMBER.REQUIRED")
					});
				} else {
					setError({
						...error,
						[e.target.name]: t("PROFILE_FORM.PHONE_NUMBER.INVALID")
					});
				}
			}
		}
	}

	const validateEmail = (email) => {
		return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email);
	};

	const validateEmailId = (e) => {
		if (!validateEmail(e.target.value)) {
			setError({
				...error,
				[e.target.name]: t("PROFILE_FORM.EMAIL.ERROR.INVALID")
			});
		} else {
			setError({
				...error,
				[e.target.name]: ""
			})
		}
	}

	const handleBloodGroupSelect = (bloodGroup) => {
		setIsSelectingBloodGroup(false);
		setIsBloodGroupDropdownOpen(false);
		setProfile({
			...profile,
			bloodGroup: bloodGroup
		});
	}

	const validatePincode = (e) => {
		if (profile.address.addressLine1 !== '') {
			if (e.target.value.length !== 6) {
				setError({
					...error,
					[e.target.name]: t("PROFILE_FORM.PINCODE.INVALID")
				});
			} else {
				setError({
					...error,
					[e.target.name]: ""
				});
			}
		}
	}

	const handleCountrySelect = (countryDetails) => {
		setIsSelectingCountry(false);
		setIsCountryDropdownOpen(false);
		setProfile({
			...profile,
			address: {
				...profile.address,
				country: countryDetails.countryName,
				countryCode: countryDetails.countryCode
			}
		});

		setError({
			...error,
			country: "",
			state: "",
			city: ""
		})
	}

	const handleCountryCodeSelect = (countryDetails) => {
		setIsCountryCodeDropdownOpen(false);
		setProfile({
			...profile,
			phone: {
				...profile.phone,
				charCode: countryDetails.countryCode,
				dialingCode: countryDetails.countryDialingCode,
				countryName: countryDetails.countryName,
			}
		});
	}

	// useEffect(() => {
	//     if (profile.address.pincode.length === 6 && profile.address.country !== '' && profile.address.state === '' && profile.address.city === '') {
	//         console.log("(EditPersonalProfile) Pincode: ", profile.address.pincode);
	//         console.log("(EditPersonalProfile) Country: ", profile.address.country);
	//         // fetchPincodeDetails(profile.address.pincode, profile.address.country);
	//         getPincodeDetails(profile.address.pincode, profile.address.countryCode).then((response) => {
	//             console.log("(EditPersonalProfile) Pincode Details: ", response);
	//             if (response?.success) {
	//                 setProfile({
	//                     ...profile,
	//                     address: {
	//                         ...profile.address,
	//                         city: response.data[0]?.city,
	//                         state: response.data[0]?.state
	//                     }
	//                 });
	//             }
	//         });
	//     }
	// }, [profile.address.pincode, profile.address.country]);

	const handleHomePhoneCountryCodeSelect = (countryDetails) => {
		setIsSelectingHomePhoneCountryCode(false);
		setIsHomePhoneCountryCodeDropdownOpen(false);
		setProfile({
			...profile,
			homePhone: {
				...profile.homePhone,
				charCode: countryDetails.countryCode,
				dialingCode: countryDetails.countryDialingCode,
				countryName: countryDetails.countryName,
			}
		});
	}

	const handleOfficePhoneCountryCodeSelect = (countryDetails) => {
		setIsSelectingOfficePhoneCountryCode(false);
		setIsOfficePhoneCountryCodeDropdownOpen(false);
		setProfile({
			...profile,
			officePhone: {
				...profile.officePhone,
				charCode: countryDetails.countryCode,
				dialingCode: countryDetails.countryDialingCode,
				countryName: countryDetails.countryName,
			}
		});
	}

	const handleFormData = (e) => {
		console.log(e.target.name + ': ' + e.target.value);

		setProfile({
			...profile,
			[e.target.name]: e.target.value
		});

		setError({
			...error,
			[e.target.name]: ""
		});
	}

	const validate = () => {
		let err = {};

		if (!profile.deceased && profile.email === '') {
			err['email'] = t("PROFILE_FORM.EMAIL.REQUIRED");
		}

		if (profile.familyTreeName === '') {
			err['familyTreeName'] = t("PROFILE_FORM.FAMILY_TREE_NAME.ERROR");
		}

		if (profile.firstName === '') {
			err['firstName'] = t("PROFILE_FORM.NAME.ENGLISH.FIRST_NAME_ERROR");
		}

		if (profile.middleName === '') {
			err['middleName'] = t("PROFILE_FORM.NAME.ENGLISH.MIDDLE_NAME_ERROR");
		}

		if (profile.firstNameDevanagariScript === '') {
			err['firstNameDevanagariScript'] = t("PROFILE_FORM.NAME.DEVANAGARI.FIRST_NAME_ERROR");
		}

		if (profile.middleNameDevanagariScript === '') {
			err['middleNameDevanagariScript'] = t("PROFILE_FORM.NAME.DEVANAGARI.MIDDLE_NAME_ERROR");
		}

		if (profile.gender === '') {
			err['gender'] = t("PROFILE_FORM.GENDER.ERROR");
		}

		if (profile.dateOfBirth === '') {
			err['dateOfBirth'] = t("PROFILE_FORM.DOB.ERROR");
		}

		if (profile.maritalStatus === 'Married' || profile.maritalStatus === 'Widowed' || profile.maritalStatus === 'Widower' || profile.maritalStatus === 'Divorced') {
			if (profile.dateOfMarriage === '') {
				err['dateOfMarriage'] = t("PROFILE_FORM.DOM.ERROR");
			}

			if (profile.gender === 'Female') {
				if (profile.sideOfFamily === "MaherVashin") {
					if (profile.firstNameAfterMarriage === '') {
						err['firstNameAfterMarriage'] = t("PROFILE_FORM.NAME.AFTER_MARRIAGE.FIRST_NAME_ERROR");
					}

					if (profile.spouseNameAfterMarriage === '') {
						err['spouseNameAfterMarriage'] = t("PROFILE_FORM.NAME.AFTER_MARRIAGE.SPOUSE_NAME_ERROR");
					}

					if (profile.lastNameAfterMarriage === '') {
						err['lastNameAfterMarriage'] = t("PROFILE_FORM.NAME.AFTER_MARRIAGE.LAST_NAME_ERROR");
					}
				} else {
					if (profile.firstNameBeforeMarriage === '') {
						err['firstNameBeforeMarriage'] = t("PROFILE_FORM.NAME.BEFORE_MARRIAGE.FIRST_NAME_ERROR");
					}

					if (profile.fathersNameBeforeMarriage === '') {
						err['fathersNameBeforeMarriage'] = t("PROFILE_FORM.NAME.BEFORE_MARRIAGE.FATHERS_NAME_ERROR");
					}

					if (profile.lastNameBeforeMarriage === '') {
						err['lastNameBeforeMarriage'] = t("PROFILE_FORM.NAME.BEFORE_MARRIAGE.LAST_NAME_ERROR");
					}
				}
			}
		}

		if (!profile.deceased && !checkAge(profile.dateOfBirth)) {
			if (profile.phone.number === '') {
				err['phone'] = t("PROFILE_FORM.PHONE_NUMBER.REQUIRED");
			} else if (profile.phone.number?.length !== 10 && !isNumberValid(profile.phone.number)) {
				err['phone'] = t("PROFILE_FORM.PHONE_NUMBER.INVALID");
			}
		}

		if (!profile.deceased && profile.homePhone.number !== '' && profile.homePhone.number?.length !== 10 && !isNumberValid(profile.homePhone.number)) {
			err['homePhone'] = t("PROFILE_FORM.HOME_PHONE_NUMBER.INVALID");
		}

		if (!profile.deceased && profile.officePhone.number !== '' && profile.officePhone.number?.length !== 10 && !isNumberValid(profile.officePhone.number)) {
			err['officePhone'] = t("PROFILE_FORM.OFFICE_PHONE_NUMBER.INVALID");
		}

		// if (profile.homePhone.number === '') {
		//     profile.homePhone = {
		//         charCode: 'IN',
		//         dialingCode: '+91',
		//         countryName: 'India',
		//         number: ''
		//     }
		// }
		//
		// if (profile.officePhone.number === '') {
		//     profile.officePhone = {
		//         charCode: 'IN',
		//         dialingCode: '+91',
		//         countryName: 'India',
		//         number: ''
		//     }
		// }

		if (!profile.deceased && profile.address.addressLine1 !== '') {
			if (profile.address.pincode === '') {
				err['pincode'] = t("PROFILE_FORM.PINCODE.REQUIRED");
			}

			if (profile.address.city === '') {
				err['city'] = t("PROFILE_FORM.CITY.REQUIRED");
			}

			if (profile.address.state === '') {
				err['state'] = t("PROFILE_FORM.STATE.REQUIRED");
			}

			if (profile.address.country === '') {
				err['country'] = t("PROFILE_FORM.COUNTRY.REQUIRED");
			}
		}

		if (!profile.deceased && profile.address.addressLine1 === '') {
			if (
				profile.address.pincode !== '' ||
				profile.address.city !== '' ||
				profile.address.state !== '' ||
				profile.address.country !== '' ||
				profile.address.countryCode !== ''
			) {
				err['addressLine1'] = t("PROFILE_FORM.ADDRESS.REQUIRED");

				if (profile.address.pincode === '') {
					err['pincode'] = t("PROFILE_FORM.PINCODE.REQUIRED");
				}

				if (profile.address.city === '') {
					err['city'] = t("PROFILE_FORM.CITY.REQUIRED");
				}

				if (profile.address.state === '') {
					err['state'] = t("PROFILE_FORM.STATE.REQUIRED");
				}

				if (profile.address.country === '') {
					err['country'] = t("PROFILE_FORM.COUNTRY.REQUIRED");
				}
			}
		}

		return err;
	}

	const handleSubmit = () => {
		console.log("Handle Submit Begin!");

		setIsSaveButtonDisabled(true);
		// const err = validate();

		// if (Object.keys(err).length > 0) {
		// 	setError(err);
		// 	setIsSaveButtonDisabled(false);
		// 	return;
		// }

		// Populate new profile data
		const updatedAccountDetails = {
			...profile,
			imageUrl: profile.imageUrl.length <= 2 ? (profile.firstName[0] + (profile.middleName ? profile.middleName[0] : "")) : profile.imageUrl,
		};

		// Update accountDetails with the new profile data
		updateAccountById(axiosPrivate, accountDetails?._id, updatedAccountDetails)
			.then((response) => {
				console.log("(EditPersonalProfile) Update Account By Id Response: ", response);

				if (response?.success) {
					if (accountDetails._id === account._id) {
						setAccount(response.data);
					} else {
						setAccountDetails(response.data);
					}

					toast.dismiss();
					toast.success(response.message);

					setShowEditPersonalProfileForm(false);
					setIsSaveButtonDisabled(false);
					setError({});
				} else {
					toast.dismiss();
					toast.error(response.message);
				}
			})
			.catch((error) => {
				console.log("(EditPersonalProfile) Update Account By Id Error: ", error);
			});
	};

	return (
		<Modal isOpen={showEditPersonalProfileForm}>
			<div className="form-modal-container">
				{/* Header */}
				<div className="form-modal-header-container">
					{/* Header Title | Close Button */}
					<div className={"flex flex-row items-center justify-between"}>
						{/* Form Title */}
						<h2>
							{t("PROFILE_FORM.EDIT_TITLE")}
						</h2>

						{/* Close Button */}
						<button
							onClick={() => {
								setShowEditPersonalProfileForm(false);
								setTimeout(() => {
									setProfile({
										isMember: accountDetails?.isMember,
										membershipNumber: accountDetails?.membershipNumber,
										pageNumber: accountDetails?.pageNumber,
										granth2010PageNumber: accountDetails?.granth2010PageNumber,
										generation: accountDetails?.generation,

										email: accountDetails?.email,

										firstName: accountDetails?.firstName,
										middleName: accountDetails?.middleName,

										firstNameDevanagariScript: accountDetails?.firstNameDevanagariScript,
										middleNameDevanagariScript: accountDetails?.middleNameDevanagariScript,

										deceased: accountDetails?.deceased,

										dateOfBirth: extractDate(accountDetails?.dateOfBirth),
										gender: accountDetails?.gender,
										bloodGroup: accountDetails?.bloodGroup,

										phone: {
											charCode: accountDetails?.phone?.charCode || 'IN',
											dialingCode: accountDetails?.phone?.dialingCode || '+91',
											countryName: accountDetails?.phone?.countryName || 'India',
											number: accountDetails?.phone?.number || '',
										},

										homePhone: {
											charCode: accountDetails?.homePhone?.charCode || 'IN',
											dialingCode: accountDetails?.homePhone?.dialingCode || '+91',
											countryName: accountDetails?.homePhone?.countryName || 'India',
											number: accountDetails?.homePhone?.number || '',
										},

										officePhone: {
											charCode: accountDetails?.officePhone?.charCode || 'IN',
											dialingCode: accountDetails?.officePhone?.dialingCode || '+91',
											countryName: accountDetails?.officePhone?.countryName || 'India',
											number: accountDetails?.officePhone?.number || '',
										},

										maritalStatus: accountDetails?.maritalStatus || 'Single',
										sideOfFamily: accountDetails?.sideOfFamily || '',

										firstNameAfterMarriage: accountDetails?.firstNameAfterMarriage || '',
										spouseNameAfterMarriage: accountDetails?.spouseNameAfterMarriage || '',
										lastNameAfterMarriage: accountDetails?.lastNameAfterMarriage || '',

										firstNameBeforeMarriage: accountDetails?.firstNameBeforeMarriage || '',
										fathersNameBeforeMarriage: accountDetails?.fathersNameBeforeMarriage || '',
										lastNameBeforeMarriage: accountDetails?.lastNameBeforeMarriage || '',

										dateOfMarriage: extractDate(accountDetails?.dateOfMarriage),

										dateOfDeath: extractDate(accountDetails?.dateOfDeath),

										familyTreeName: accountDetails?.familyTreeName || '',

										biography: accountDetails?.biography || '',

										address: {
											addressLine1: accountDetails?.address?.addressLine1 || '',
											pincode: accountDetails?.address?.pincode || '',
											city: accountDetails?.address?.city || '',
											state: accountDetails?.address?.state || '',
											country: accountDetails?.address?.country || '',
											countryCode: accountDetails?.address?.countryCode || '',
										},

										imageUrl: accountDetails?.imageUrl || '',
										imageUrlBg: accountDetails?.imageUrlBg || '',
									});
								}, 300);
								setError({});
							}}
						>
							<img
								className={"w-[75%] aspect-square"}
								src={"/assets/close-icon.png"}
								alt={"Close"}
								title={"Close"}
							/>
						</button>
					</div>

					{/* Divider */}
					<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full"}/>
				</div>

				{/* Form Fields */}
				<div className="form-modal-body-container">
					{/* Membership Number | Page Number | Generation */}
					{/*<fieldset>*/}
					{/*    <div className="flex gap-4">*/}
					{/*        /!* Membership Number *!/*/}
					{/*        <div className="flex-1 flex flex-col gap-2">*/}
					{/*            <label*/}
					{/*                className=""*/}
					{/*                htmlFor="membershipNumber"*/}
					{/*            >*/}
					{/*                Membership No.*/}
					{/*            </label>*/}
					{/*            <input*/}
					{/*                // disabled={!((profile.address.pincode.length === 6) && profile.address.country)}*/}
					{/*                id="membershipNumber"*/}
					{/*                className="w-full font-custom-medium inline-flex min-h-[5vh] max-h-[5vh] rounded-md px-[0.5vw] text-[1vw] leading-none outline-none border-2 border-gray-300 focus:border-[#5F0F40]"*/}
					{/*                name='membershipNumber'*/}
					{/*                value={profile.membershipNumber}*/}
					{/*                onChange={(e) => {*/}
					{/*                    setProfile({*/}
					{/*                        ...profile,*/}
					{/*                        membershipNumber: e.target.value,*/}
					{/*                        isMember: e.target.value !== ""*/}
					{/*                    });*/}
					{/*                }}*/}
					{/*            />*/}
					{/*        </div>*/}

					{/*        /!* Page Number *!/*/}
					{/*        <div className="flex-1 flex flex-col gap-2">*/}
					{/*            <label*/}
					{/*                className=""*/}
					{/*                htmlFor="pageNumber"*/}
					{/*            >*/}
					{/*                Page No.*/}
					{/*            </label>*/}
					{/*            <input*/}
					{/*                // disabled={!((profile.address.pincode.length === 6) && profile.address.country)}*/}
					{/*                id="pageNumber"*/}
					{/*                className="w-full font-custom-medium inline-flex min-h-[5vh] max-h-[5vh] rounded-md px-[0.5vw] text-[1vw] leading-none outline-none border-2 border-gray-300 focus:border-[#5F0F40]"*/}
					{/*                name='pageNumber'*/}
					{/*                value={profile.pageNumber}*/}
					{/*                onChange={(e) => {*/}
					{/*                    setProfile({*/}
					{/*                        ...profile,*/}
					{/*                        pageNumber: e.target.value*/}
					{/*                    });*/}
					{/*                }}*/}
					{/*            />*/}
					{/*        </div>*/}

					{/*        /!* Generation *!/*/}
					{/*        <div className="flex-1 flex flex-col gap-2">*/}
					{/*            <label*/}
					{/*                className=""*/}
					{/*                htmlFor="generation"*/}
					{/*            >*/}
					{/*                Generation*/}
					{/*            </label>*/}
					{/*            <input*/}
					{/*                // disabled={!((profile.address.pincode.length === 6) && profile.address.country)}*/}
					{/*                id="generation"*/}
					{/*                className="w-full font-custom-medium inline-flex min-h-[5vh] max-h-[5vh] rounded-md px-[0.5vw] text-[1vw] leading-none outline-none border-2 border-gray-300 focus:border-[#5F0F40]"*/}
					{/*                name='generation'*/}
					{/*                value={profile.generation}*/}
					{/*                onChange={(e) => {*/}
					{/*                    setProfile({*/}
					{/*                        ...profile,*/}
					{/*                        generation: e.target.value*/}
					{/*                    });*/}
					{/*                }}*/}
					{/*            />*/}
					{/*        </div>*/}
					{/*    </div>*/}
					{/*</fieldset>*/}

					{/* Email Input */}
					{
						!profile.deceased && (
							<fieldset>
								<div className="custom-input-container">
									<label
										htmlFor="email"
										className=""
									>
										{t("PROFILE_FORM.EMAIL.LABEL")}
									</label>
									<input
										type="email"
										id="email"
										name='email'
										className="custom-input"
										value={profile.email}
										onChange={handleFormData}
										onBlur={validateEmailId}
										required
									/>
									{
										error['email'] &&
										<p
											className="custom-input-error-message"
											id="error"
										>
											{error['email']}
										</p>
									}
								</div>
							</fieldset>
						)
					}

					{/* Name in English */}
					<fieldset>
						<div className="custom-input-container">
							<label
								className=""
								htmlFor="firstName"
							>
								{t("PROFILE_FORM.NAME.ENGLISH.LABEL")}
							</label>
							<div className="custom-input-multiple-fields-container">
								{/* First Name */}
								<div className="flex-1 flex flex-col">
									<input
										id="firstName"
										className="custom-input"
										name='firstName'
										placeholder={t("PROFILE_FORM.NAME.PLACEHOLDER.FIRST_NAME")}
										value={profile.firstName}
										onChange={handleFormData}
									/>
									{
										error['firstName'] &&
										<span
											className="custom-input-error-message"
											id="error"
										>
                                            {error['firstName']}
                                        </span>
									}
								</div>

								{/* Father's Name */}
								<div className="flex-1 flex flex-col">
									<input
										className="custom-input"
										id="middleName"
										name='middleName'
										placeholder={t("PROFILE_FORM.NAME.PLACEHOLDER.MIDDLE_NAME")}
										value={profile.middleName}
										onChange={handleFormData}
									/>
									{
										error['middleName'] &&
										<p className="custom-input-error-message"
										   id="error">
											{error['middleName']}
										</p>
									}
								</div>
							</div>
						</div>
					</fieldset>

					{/* Name in Devnagari */}
					<fieldset>
						<div className="custom-input-container">
							<label
								className=""
								htmlFor="firstNameDevanagariScript"
							>
								{t("PROFILE_FORM.NAME.DEVANAGARI.LABEL")}
							</label>
							<div className="custom-input-multiple-fields-container">
								{/* First Name in Devnagari */}
								<div className={"flex-1 flex flex-col"}>
									<input
										className="custom-input"
										id="firstNameDevanagariScript"
										name='firstNameDevanagariScript'
										placeholder={t("PROFILE_FORM.NAME.PLACEHOLDER.FIRST_NAME")}
										value={profile.firstNameDevanagariScript}
										onChange={handleFormData}
									/>
									{
										error['firstNameDevanagariScript'] &&
										<p className="custom-input-error-message"
										   id="error">
											{error['firstNameDevanagariScript']}
										</p>
									}
								</div>

								{/* Father's Name in Devnagari */}
								<div className={"flex-1 flex flex-col"}>
									<input
										className="custom-input"
										name='middleNameDevanagariScript'
										placeholder={t("PROFILE_FORM.NAME.PLACEHOLDER.MIDDLE_NAME")}
										value={profile.middleNameDevanagariScript}
										onChange={handleFormData}
									/>
									{
										error['middleNameDevanagariScript'] &&
										<p className="custom-input-error-message"
										   id="error">
											{error['middleNameDevanagariScript']}
										</p>
									}
								</div>
							</div>
						</div>
					</fieldset>

					{/* Deceased */}
					{
						// account._id === accountDetails.familyTreeAdminId &&
						window.location.pathname.includes('public-profile') &&
						(
							<fieldset>
								<label className="custom-input-label-checkbox">
									<input
										type="checkbox"
										name='deceased'
										checked={profile.deceased}
										onChange={() => {
											setProfile({
												...profile,
												deceased: !profile.deceased
											});
										}}
									/>
									<p className="">
										{t("PROFILE_FORM.DECEASED")}
									</p>
									<img
										className=""
										src={"/assets/deceased-icon.png"}
										alt={"Deceased"}
									/>
								</label>
							</fieldset>
						)
					}

					{/*  DOB | Gender | Blood Group */}
					<fieldset>
						<div className="custom-input-multiple-fields-container">
							{/* Date of Birth */}
							<div className="custom-input-container">
								<label
									className=''
									htmlFor='dateOfBirth'
								>
									{t("PROFILE_FORM.DOB.LABEL")}
								</label>
								<div className="relative w-full">
									<input
										type="date"
										name='dateOfBirth'
										required
										value={profile.dateOfBirth}
										onChange={handleFormData}
										className={`custom-input`}
									/>
									{
										error['dateOfBirth'] &&
										<p className="custom-input-error-message"
										   id="error">
											{error['dateOfBirth']}
										</p>
									}
								</div>
							</div>

							{/* Gender */}
							<div className="custom-input-container">
								<label
									className=""
									htmlFor="gender"
								>
									{t("PROFILE_FORM.GENDER.LABEL")}
								</label>

								<div className="w-full h-full flex flex-col">
									<button
										onBlur={() => {
											if (!isSelectingGender) {
												setIsGenderDropdownOpen(false);
											}
										}}
										className={`custom-input-dropdown-button
                                        ${isGenderDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
										onClick={() => setIsGenderDropdownOpen(!isGenderDropdownOpen)}
									>
										<div className={""}>
											<p className={""}>
												{
													profile.gender === "Male"
														? t("PROFILE_FORM.GENDER.MALE")
														: t("PROFILE_FORM.GENDER.FEMALE")
												}
											</p>
											<img
												className={""}
												src={profile.gender === "Male" ? "/assets/male-gender-icon.png" : "/assets/female-gender-icon.png"}
												alt={profile.gender === "Male" ? "Male" : "Female"}
											/>
										</div>
										<ChevronDownIcon
											className={`${isGenderDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
											aria-hidden="true"
											stroke="#292D32"
											strokeWidth={"0.1vh"}
										/>
									</button>

									{/* Gender Dropdown */}
									<div className={`relative`}>
										<Transition show={isGenderDropdownOpen}>
											<div
												className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                    origin-top-right rounded-md shadow-2xl 
                                                    border border-gray-700 overflow-y-scroll
                                                    data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                    data-[enter]:duration-100 data-[enter]:ease-out 
                                                    data-[leave]:duration-75 data-[leave]:ease-in`}
											>
												<button
													className="custom-input-dropdown-list-button"
													onMouseDown={() => setIsSelectingGender(true)}
													onClick={() => handleGenderSelect("Male")}
												>
													<p className={""}>
														{t("PROFILE_FORM.GENDER.MALE")}
													</p>

													<img
														className={""}
														src={"/assets/male-gender-icon.png"}
														alt={"Male"}
													/>
												</button>
												<button
													className="custom-input-dropdown-list-button"
													onMouseDown={() => setIsSelectingGender(true)}
													onClick={() => handleGenderSelect("Female")}
												>
													<p className={""}>
														{t("PROFILE_FORM.GENDER.FEMALE")}
													</p>

													<img
														className={""}
														src={"/assets/female-gender-icon.png"}
														alt={"Female"}
													/>
												</button>
											</div>
										</Transition>
									</div>
								</div>
							</div>

							{/* Date of Death | Blood Group */}
							{
								profile.deceased
									?
									// Date of Death
									<div className="custom-input-container">
										<label
											className=''
											htmlFor='dateOfDeath'
										>
											{t("PROFILE_FORM.DOD.LABEL")}
										</label>
										<div className="relative w-full">
											<input
												type="date"
												name='dateOfDeath'
												required
												value={profile.dateOfDeath}
												onChange={handleFormData}
												className="custom-input"
											/>
											{
												error['dateOfDeath'] &&
												<p className="custom-input-error-message"
												   id="error">
													{error['dateOfDeath']}
												</p>
											}
										</div>
									</div>
									:
									// Blood Group
									<div className={"custom-input-container"}>
										<label
											className=""
											htmlFor="bloodGroup"
										>
											{t("PROFILE_FORM.BLOOD_GROUP.LABEL")}
										</label>
										<div className="w-full h-full flex flex-col">
											<button
												onBlur={() => {
													if (!isSelectingBloodGroup) {
														setIsBloodGroupDropdownOpen(false);
													}
												}}
												className={`custom-input-dropdown-button
                                                    ${isBloodGroupDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
												onClick={() => setIsBloodGroupDropdownOpen(!isBloodGroupDropdownOpen)}
											>
												<p className={""}>
													{profile.bloodGroup}
												</p>
												<ChevronDownIcon
													className={`${isBloodGroupDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
													aria-hidden="true"
													stroke="#292D32"
													strokeWidth={"0.1vh"}
												/>
											</button>

											{/* Blood Group Dropdown */}
											<div className={`relative`}>
												<Transition show={isBloodGroupDropdownOpen}>
													<div
														className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                        origin-top-right rounded-md shadow-2xl 
                                                        border border-gray-700 overflow-y-scroll
                                                        data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                        data-[enter]:duration-100 data-[enter]:ease-out 
                                                        data-[leave]:duration-75 data-[leave]:ease-in`}
													>
														{
															bloodGroups.map((bloodGroup, index) => (
																<button
																	key={index}
																	className="custom-input-dropdown-list-button"
																	onMouseDown={() => setIsSelectingBloodGroup(true)}
																	onClick={() => handleBloodGroupSelect(bloodGroup)}
																>
																	<p className={""}>
																		{bloodGroup}
																	</p>
																</button>
															))
														}
													</div>
												</Transition>
											</div>
										</div>
									</div>
							}
						</div>
					</fieldset>

					{/* Blood Group | Mobile Number | Marital Status */}
					<fieldset>
						<div className={`custom-input-multiple-fields-container`}>
							{/* Blood Group | Mobile Number Input */}
							{
								profile.deceased
									?
									// Blood Group
									<div className={"custom-input-container"}>
										<label
											className=""
											htmlFor="bloodGroup"
										>
											{t("PROFILE_FORM.BLOOD_GROUP.LABEL")}
										</label>
										<div className="w-full h-full flex flex-col">
											<button
												onBlur={() => {
													if (!isSelectingBloodGroup) {
														setIsBloodGroupDropdownOpen(false);
													}
												}}
												className={`custom-input-dropdown-button
                                                    ${isBloodGroupDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
												onClick={() => setIsBloodGroupDropdownOpen(!isBloodGroupDropdownOpen)}
											>
												<p className={""}>
													{profile.bloodGroup}
												</p>
												<ChevronDownIcon
													className={`${isBloodGroupDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
													aria-hidden="true"
													stroke="#292D32"
													strokeWidth={"0.1vh"}
												/>
											</button>

											{/* Blood Group Dropdown */}
											<div className={`relative`}>
												<Transition show={isBloodGroupDropdownOpen}>
													<div
														className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                        origin-top-right rounded-md shadow-2xl 
                                                        border border-gray-700 overflow-y-scroll
                                                        data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                        data-[enter]:duration-100 data-[enter]:ease-out 
                                                        data-[leave]:duration-75 data-[leave]:ease-in`}
													>
														{
															bloodGroups.map((bloodGroup, index) => (
																<button
																	key={index}
																	className="custom-input-dropdown-list-button"
																	onMouseDown={() => setIsSelectingBloodGroup(true)}
																	onClick={() => handleBloodGroupSelect(bloodGroup)}
																>
																	<p className={""}>
																		{bloodGroup}
																	</p>
																</button>
															))
														}
													</div>
												</Transition>
											</div>
										</div>
									</div>
									:
									// Phone Number Input
									<div className={"custom-phone-number-input"}>
										<label htmlFor="phone">
											{
												checkAge(profile.dateOfBirth)
													? t("PROFILE_FORM.PHONE_NUMBER.LABEL")
													: t("PROFILE_FORM.PHONE_NUMBER.LABEL_REQUIRED")
											}
										</label>
										<div className="w-full h-full flex flex-col">
											<div
												className="custom-phone-number-input-container"
											>
												<button
													onBlur={() => {
														if (!isSelectingCountryCode) {
															setIsCountryCodeDropdownOpen(false);
														}
													}}
													onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
												>
													<img
														src={`/assets/country_flags/${profile.phone.charCode}.png`}
														alt={profile.phone.countryName}
													/>
													<p aria-selected={"false"}>
														{profile.phone.dialingCode}
													</p>
													<ChevronDownIcon
														// onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
														className={`${isCountryCodeDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
														aria-hidden="true"
														stroke="#292D32"
														strokeWidth={"0.1vh"}
													/>
												</button>
												<input
													type="phone"
													name="phone"
													placeholder="7734567890"
													value={profile.phone.number || ""}
													onChange={(e) => {
														setProfile({
															...profile,
															phone: {
																...profile.phone,
																number: e.target.value
															}
														});

														setError({
															...error,
															[e.target.name]: ""
														});
													}}
													onBlur={validatePhoneNumber}
													required
												/>
											</div>

											{/* Country Code Dropdown */}
											<div className={"relative"}>
												<Transition show={isCountryCodeDropdownOpen}>
													<div
														className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                            origin-top-right rounded-md shadow-2xl 
                                                            border border-gray-700 overflow-y-scroll
                                                            data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                            data-[enter]:duration-100 data-[enter]:ease-out 
                                                            data-[leave]:duration-75 data-[leave]:ease-in`}
													>
														{
															countryCodes.map((country, index) => (
																<button
																	key={index}
																	className="custom-phone-number-input-dropdown-button hover:bg-gray-300"
																	onMouseDown={() => setIsSelectingCountryCode(true)}
																	onClick={() => handleCountryCodeSelect(country)}
																>
																	<img
																		src={country.countryFlagSrc}
																		className={""}
																		alt={country.countryName}
																	/>
																	<p
																		className={"text-left"}
																		aria-selected={"false"}
																	>
																		{country.countryName}
																	</p>
																	<p
																		className={""}
																		aria-selected={"false"}
																	>
																		{'(' + country.countryDialingCode + ')'}
																	</p>
																</button>
															))
														}
													</div>
												</Transition>
											</div>
										</div>
										{
											error['phone'] &&
											<p className="custom-input-error-message"
											   id="error">
												{error['phone']}
											</p>
										}
									</div>
							}

							{/* Marital Status */}
							<div
								className={`custom-input-container
                                ${profile.deceased ? '' : 'portrait:!w-full portrait:md:!w-2/5 landscape:!w-2/5'}`}
							>
								<label
									className=""
									htmlFor="maritalStatus"
								>
									<img
										src={"/assets/marriage-icon.png"}
										className={""}
										alt={"Marriage"}
										title={"Marriage"}
									/>
									{t("PROFILE_FORM.MARITAL_STATUS.LABEL")}
								</label>
								<div className="w-full h-full flex flex-col">
									<button
										onBlur={() => {
											if (!isSelectingMaritalStatus) {
												setIsMaritalStatusDropdownOpen(false);
											}
										}}
										className={`custom-input-dropdown-button
                                            ${isGenderDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
										onClick={() => setIsMaritalStatusDropdownOpen(!isMaritalStatusDropdownOpen)}
									>
										<p className={""}>
											{maritalStatusLabel(profile.maritalStatus)}
										</p>
										<ChevronDownIcon
											className={`${isMaritalStatusDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
											aria-hidden="true"
											stroke="#292D32"
											strokeWidth={"0.1vh"}
										/>
									</button>

									{/* Marital Status Dropdown */}
									<div className={"relative"}>
										<Transition show={isMaritalStatusDropdownOpen}>
											<div
												className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                    origin-top-right rounded-md shadow-2xl 
                                                    border border-gray-700 overflow-y-scroll
                                                    data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                    data-[enter]:duration-100 data-[enter]:ease-out 
                                                    data-[leave]:duration-75 data-[leave]:ease-in`}
											>
												<button
													className="custom-input-dropdown-list-button"
													onMouseDown={() => setIsSelectingMaritalStatus(true)}
													onClick={() => handleMaritalStatusSelect("Minor")}
												>
													<p className={""}>
														{t("PROFILE_FORM.MARITAL_STATUS.MINOR")}
													</p>
												</button>
												<button
													className="custom-input-dropdown-list-button"
													onMouseDown={() => setIsSelectingMaritalStatus(true)}
													onClick={() => handleMaritalStatusSelect("Single")}
												>
													<p className={""}>
														{t("PROFILE_FORM.MARITAL_STATUS.SINGLE")}
													</p>
												</button>
												<button
													className="custom-input-dropdown-list-button"
													onMouseDown={() => setIsSelectingMaritalStatus(true)}
													onClick={() => handleMaritalStatusSelect("Married")}
												>
													<p className={""}>
														{t("PROFILE_FORM.MARITAL_STATUS.MARRIED")}
													</p>
												</button>
												<button
													className="custom-input-dropdown-list-button"
													onMouseDown={() => setIsSelectingMaritalStatus(true)}
													onClick={() => handleMaritalStatusSelect(profile.gender === "Male" ? "Widower" : "Widowed")}
												>
													<p className={""}>
														{
															profile.gender === "Male"
																? t("PROFILE_FORM.MARITAL_STATUS.WIDOWER")
																: t("PROFILE_FORM.MARITAL_STATUS.WIDOWED")
														}
													</p>
												</button>
												<button
													className="custom-input-dropdown-list-button"
													onMouseDown={() => setIsSelectingMaritalStatus(true)}
													onClick={() => handleMaritalStatusSelect("Divorced")}
												>
													<p className={""}>
														{t("PROFILE_FORM.MARITAL_STATUS.DIVORCED")}
													</p>
												</button>
											</div>
										</Transition>
									</div>
									{
										error['maritalStatus'] &&
										<p className="custom-input-error-message"
										   id="error">
											{error['maritalStatus']}
										</p>
									}
								</div>
							</div>

							{/* Date of Marriage */}
							{
								(profile.maritalStatus === 'Married' || profile.maritalStatus === 'Widowed' || profile.maritalStatus === 'Widower' || profile.maritalStatus === 'Divorced') &&
								<div className="custom-input-container !flex-[0]">
									<label
										className=""
										htmlFor='dateOfMarriage'
									>
										{t("PROFILE_FORM.DOM.LABEL")}
									</label>
									<div className="relative w-full">
										<input
											type="date"
											name='dateOfMarriage'
											required
											value={profile.dateOfMarriage}
											onChange={handleFormData}
											className={`custom-input`}
										/>
										{
											error['dateOfMarriage'] &&
											<p className="custom-input-error-message"
											   id="error">
												{error['dateOfMarriage']}
											</p>
										}
									</div>
								</div>
							}
						</div>
					</fieldset>

					{/* Side Of Family | Name Before / After Marriage */}
					{
						(profile.maritalStatus === 'Married' || profile.maritalStatus === 'Widowed' || profile.maritalStatus === 'Widower' || profile.maritalStatus === 'Divorced')
						&& profile.gender === "Female" && (
							<fieldset>
								<div className={"custom-input-multiple-fields-container"}>
									{/* Side Of Family */}
									<div
										className="custom-input-container portrait:!w-full portrait:md:!w-1/4 landscape:!w-1/4">
										<label
											className=""
											htmlFor="maritalStatus"
										>
											{t("PROFILE_FORM.SIDE_OF_FAMILY.LABEL")}
										</label>
										<div className="w-full h-full flex flex-col">
											<button
												disabled={profile.gender === "Female" &&
													(profile.maritalStatus === 'Single' || profile.maritalStatus === 'Minor')}
												onBlur={() => {
													if (!isSelectingSideOfFamily) {
														setIsSideOfFamilyDropdownOpen(false);
													}
												}}
												className={`custom-input-dropdown-button
                                                    ${isSideOfFamilyDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
												onClick={() => setIsSideOfFamilyDropdownOpen(!isSideOfFamilyDropdownOpen)}
											>
												<p className={""}>
													{
														profile.sideOfFamily !== "" &&
														(profile.sideOfFamily === "MaherVashin"
															? t("PROFILE_FORM.SIDE_OF_FAMILY.MAHER_VASHIN")
															: t("PROFILE_FORM.SIDE_OF_FAMILY.SASUR_VASHIN"))
													}
												</p>
												<ChevronDownIcon
													className={`${isSideOfFamilyDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
													aria-hidden="true"
													stroke="#292D32"
													strokeWidth={"0.1vh"}
												/>
											</button>

											{/* Side Of Family Dropdown */}
											<div className={"relative"}>
												<Transition show={isSideOfFamilyDropdownOpen}>
													<div
														className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                            origin-top-right rounded-md shadow-2xl 
                                                            border border-gray-700 overflow-y-scroll
                                                            data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                            data-[enter]:duration-100 data-[enter]:ease-out 
                                                            data-[leave]:duration-75 data-[leave]:ease-in`}
													>
														<button
															className={`custom-input-dropdown-list-button`}
															onMouseDown={() => setIsSelectingSideOfFamily(true)}
															onClick={() => handleSideOfFamilySelect("MaherVashin")}
														>
															<p className={""}>
																{t("PROFILE_FORM.SIDE_OF_FAMILY.MAHER_VASHIN")}
															</p>
														</button>
														<button
															className={`custom-input-dropdown-list-button`}
															onMouseDown={() => setIsSelectingMaritalStatus(true)}
															onClick={() => handleSideOfFamilySelect("SasurVashin")}
														>
															<p className={""}>
																{t("PROFILE_FORM.SIDE_OF_FAMILY.SASUR_VASHIN")}
															</p>
														</button>
													</div>
												</Transition>
											</div>
										</div>
									</div>

									{/* Name Before Marriage */}
									{
										profile.sideOfFamily === "SasurVashin" && (
											<div className="custom-input-container">
												<label
													className=""
													htmlFor="nameBeforeMarriage"
												>
													{t("PROFILE_FORM.NAME.BEFORE_MARRIAGE.LABEL")}
												</label>

												<div className="custom-input-multiple-fields-container">
													{/* First Name */}
													<div className={"flex-1 flex flex-col"}>
														<input
															id="firstNameBeforeMarriage"
															type="text"
															className={`custom-input`}
															placeholder={t("PROFILE_FORM.NAME.BEFORE_MARRIAGE.FIRST_NAME_PLACEHOLDER")}
															name='firstNameBeforeMarriage'
															value={profile.firstNameBeforeMarriage || ""}
															onChange={handleFormData}
														/>
														{
															error['firstNameBeforeMarriage'] &&
															<p className="custom-input-error-message"
															   id="error">
																{error['firstNameBeforeMarriage']}
															</p>
														}
													</div>

													{/* Father's Name */}
													<div className={"flex-1 flex flex-col"}>
														<input
															id="fathersNameBeforeMarriage"
															type="text"
															className={`custom-input`}
															placeholder={t("PROFILE_FORM.NAME.BEFORE_MARRIAGE.FATHERS_NAME_PLACEHOLDER")}
															name='fathersNameBeforeMarriage'
															value={profile.fathersNameBeforeMarriage || ""}
															onChange={handleFormData}
														/>
														{
															error['fathersNameBeforeMarriage'] &&
															<p className="custom-input-error-message"
															   id="error">
																{error['fathersNameBeforeMarriage']}
															</p>
														}
													</div>

													{/* Last Name */}
													<div className={"flex-1 flex flex-col"}>
														<input
															id="lastNameBeforeMarriage"
															type="text"
															className={`custom-input`}
															placeholder={t("PROFILE_FORM.NAME.BEFORE_MARRIAGE.LAST_NAME_PLACEHOLDER")}
															name='lastNameBeforeMarriage'
															value={profile.lastNameBeforeMarriage || ""}
															onChange={handleFormData}
														/>
														{
															error['lastNameBeforeMarriage'] &&
															<p className="custom-input-error-message"
															   id="error">
																{error['lastNameBeforeMarriage']}
															</p>
														}
													</div>
												</div>
											</div>
										)
									}

									{/* Name After Marriage */}
									{
										profile.sideOfFamily === "MaherVashin" && (
											<div className="custom-input-container">
												<label
													className=""
													htmlFor="nameAfterMarriage"
												>
													{t("PROFILE_FORM.NAME.AFTER_MARRIAGE.LABEL")}
												</label>

												<div className="custom-input-multiple-fields-container">
													{/* First Name */}
													<div className={"flex-1 flex flex-col"}>
														<input
															id="firstNameAfterMarriage"
															type="text"
															className={`custom-input`}
															placeholder={t("PROFILE_FORM.NAME.AFTER_MARRIAGE.FIRST_NAME_PLACEHOLDER")}
															name='firstNameAfterMarriage'
															value={profile.firstNameAfterMarriage || ""}
															onChange={handleFormData}
														/>
														{
															error['firstNameAfterMarriage'] &&
															<p className="custom-input-error-message"
															   id="error">
																{error['firstNameAfterMarriage']}
															</p>
														}
													</div>

													{/* Spouse Name */}
													<div className={"flex-1 flex flex-col"}>
														<input
															id="spouseNameAfterMarriage"
															type="text"
															className={`custom-input`}
															placeholder={t("PROFILE_FORM.NAME.AFTER_MARRIAGE.SPOUSE_NAME_PLACEHOLDER")}
															name='spouseNameAfterMarriage'
															value={profile.spouseNameAfterMarriage || ""}
															onChange={handleFormData}
														/>
														{
															error['spouseNameAfterMarriage'] &&
															<p className="custom-input-error-message"
															   id="error">
																{error['spouseNameAfterMarriage']}
															</p>
														}
													</div>

													{/* Last Name */}
													<div className={"flex-1 flex flex-col"}>
														<input
															id="lastNameAfterMarriage"
															type="text"
															className={`custom-input`}
															placeholder={t("PROFILE_FORM.NAME.AFTER_MARRIAGE.LAST_NAME_PLACEHOLDER")}
															name='lastNameAfterMarriage'
															value={profile.lastNameAfterMarriage || ""}
															onChange={handleFormData}
														/>
														{
															error['lastNameAfterMarriage'] &&
															<p className="custom-input-error-message"
															   id="error">
																{error['lastNameAfterMarriage']}
															</p>
														}
													</div>
												</div>
											</div>
										)
									}
								</div>
							</fieldset>
						)
					}

					{/* Biography */}
					<fieldset>
						<div className="custom-input-container">
							<label
								className=""
								htmlFor="bio"
							>
								{t("PROFILE_FORM.BIO.LABEL")}
							</label>
							<textarea
								id="description"
								rows={2}
								className={`custom-input no-scrollbar`}
								placeholder={t("PROFILE_FORM.BIO.PLACEHOLDER")}
								name='biography'
								value={profile.biography || ""}
								onChange={handleFormData}
							/>
						</div>
					</fieldset>

					{
						!profile.deceased && (
							<>
								{/* Address | Pincode */}
								<fieldset>
									<div className="custom-input-multiple-fields-container">
										{/* Address Line */}
										<div className="custom-input-container !flex-1">
											<label
												className=""
												htmlFor="addressLine1"
											>
												{t("PROFILE_FORM.ADDRESS.LABEL")}
											</label>
											<input
												id="addressLine1"
												className="custom-input"
												name='addressLine1'
												placeholder={t("PROFILE_FORM.ADDRESS.PLACEHOLDER")}
												value={profile.address.addressLine1}
												onChange={(e) => {
													setProfile({
														...profile,
														address: {
															...profile.address,
															addressLine1: e.target.value
														}
													});

													setError({
														...error,
														[e.target.name]: ""
													});
												}}
											/>
											{
												error['addressLine1'] &&
												<p className="custom-input-error-message"
												   id="error">
													{error['addressLine1']}
												</p>
											}
										</div>

										{/* Pincode */}
										<div
											className="custom-input-container portrait:w-full
                                            portrait:md:!w-1/3 landscape:!w-1/3"
										>
											<label
												className=""
												htmlFor="pincode"
											>
												{t("PROFILE_FORM.PINCODE.LABEL")}
											</label>
											<input
												type={"number"}
												max={6}
												className="custom-input"
												id="pincode"
												name='pincode'
												value={profile.address.pincode}
												onChange={(e) => {
													setProfile({
														...profile,
														address: {
															...profile.address,
															pincode: e.target.value
														}
													});

													setError({
														...error,
														[e.target.name]: ""
													});
												}}
												onBlur={validatePincode}
											/>
											{
												error['pincode'] &&
												<p className="custom-input-error-message"
												   id="error">
													{error['pincode']}
												</p>
											}
										</div>
									</div>
								</fieldset>

								{/* Country | City | State */}
								<fieldset>
									<div className="custom-input-multiple-fields-container">
										{/* Country */}
										<div className={"custom-input-container !flex-1"}>
											<label
												className=""
												htmlFor="country"
											>
												{t("PROFILE_FORM.COUNTRY.LABEL")}
											</label>
											<div className="w-full h-full flex flex-col">
												{/* Country Value */}
												<button
													onBlur={() => {
														if (!isSelectingCountry) {
															setIsCountryDropdownOpen(false);
														}
													}}
													className={`custom-input-dropdown-button
                                                        ${isCountryDropdownOpen && 'focus:ring-4 focus:ring-offset-1 focus:ring-[#5F0F40]'}`}
													onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
												>
													{/*<p className={"text-nowrap font-custom-regular text-[1vw]"}>*/}
													{/*    {profile.address.country}*/}
													{/*</p>*/}
													<input
														// disabled={!((profile.address.pincode.length === 6) && profile.address.country)}
														id="country"
														className=""
														name="country"
														value={profile.address.country}
														onChange={(e) => {
															if (e.target.value === "") {
																setFilteredCountryDetails(countryCodes);
															} else {
																console.log("Country Input Value: ", e.target.value);
																setFilteredCountryDetails(
																	countryCodes
																		.filter((country) => {
																			return country.countryName.toLowerCase().includes(e.target.value.toLowerCase());
																		})
																);
															}

															setProfile({
																...profile,
																address: {
																	...profile.address,
																	country: e.target.value
																}
															});

															setError({
																...error,
																[e.target.name]: ""
															});
														}}
													/>
													<ChevronDownIcon
														className={`${isCountryDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
														aria-hidden="true"
														stroke="#292D32"
														strokeWidth={"0.1vh"}
													/>
												</button>

												{/* Country Dropdown */}
												<div className={"relative"}>
													<Transition show={isCountryDropdownOpen}>
														<div
															className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                                origin-top-right rounded-md shadow-2xl 
                                                                border border-gray-700 overflow-y-scroll no-scrollbar
                                                                data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                data-[enter]:duration-100 data-[enter]:ease-out 
                                                                data-[leave]:duration-75 data-[leave]:ease-in`}
														>
															{
																filteredCountryDetails.length > 0
																	? filteredCountryDetails.map((countryDetails, index) => (
																		<button
																			key={index}
																			className={`custom-input-dropdown-list-button`}
																			onMouseDown={() => setIsSelectingCountry(true)}
																			onClick={() => handleCountrySelect(countryDetails)}
																		>
																			<p className={"text-left text-nowrap"}>
																				{countryDetails.countryName}
																			</p>
																		</button>
																	)) : (
																		<button
																			className={`custom-input-dropdown-list-button`}
																			onClick={() => setIsCountryDropdownOpen(false)}
																		>
																			<p className={"text-left text-nowrap"}>
																				{profile.address.country}
																			</p>
																		</button>
																	)
															}
														</div>
													</Transition>
												</div>
											</div>
											{
												error['country'] &&
												<p className="custom-input-error-message"
												   id="error">
													{error['country']}
												</p>
											}
										</div>

										{/* State */}
										<div className="custom-input-container !flex-1">
											<label
												className=""
												htmlFor="state"
											>
												{t("PROFILE_FORM.STATE.LABEL")}
											</label>
											<input
												// disabled={!((profile.address.pincode.length === 6) && profile.address.country)}
												id="state"
												className="custom-input"
												name='state'
												value={profile.address.state}
												onChange={(e) => {
													setProfile({
														...profile,
														address: {
															...profile.address,
															state: e.target.value
														}
													});

													setError({
														...error,
														[e.target.name]: ""
													});
												}}
											/>
											{
												error['state'] &&
												<p className="custom-input-error-message"
												   id="error">
													{error['state']}
												</p>
											}
										</div>

										{/* City */}
										<div className="custom-input-container !flex-1">
											<label
												className=""
												htmlFor="city"
											>
												{t("PROFILE_FORM.CITY.LABEL")}
											</label>
											<input
												// disabled={!((profile.address.pincode.length === 6) && profile.address.country)}
												id="city"
												className="custom-input"
												name='city'
												value={profile.address.city}
												onChange={(e) => {
													setProfile({
														...profile,
														address: {
															...profile.address,
															city: e.target.value
														}
													});

													setError({
														...error,
														[e.target.name]: ""
													});
												}}
											/>
											{
												error['city'] &&
												<p className="custom-input-error-message"
												   id="error">
													{error['city']}
												</p>
											}
										</div>
									</div>
								</fieldset>

								{/* Home | Office Phone Number */}
								<fieldset>
									<div className={"custom-input-multiple-fields-container"}>
										{/* Home Phone Number */}
										<div className={"custom-phone-number-input"}>
											<label
												className=""
												htmlFor="homePhone"
											>
												{t("PROFILE_FORM.HOME_PHONE_NUMBER.LABEL")}
											</label>
											<div className="w-full h-full flex flex-col">
												<div
													className="custom-phone-number-input-container"
												>
													<button
														onBlur={() => {
															if (!isSelectingHomePhoneCountryCode) {
																setIsHomePhoneCountryCodeDropdownOpen(false);
															}
														}}
														onClick={() => setIsHomePhoneCountryCodeDropdownOpen(!isHomePhoneCountryCodeDropdownOpen)}
													>
														<img
															src={`/assets/country_flags/${profile.homePhone.charCode}.png`}
															alt={profile.homePhone.countryName}
														/>
														<p
															aria-selected={"false"}
														>
															{profile.homePhone.dialingCode}
														</p>
														<ChevronDownIcon
															// onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
															className={`${isHomePhoneCountryCodeDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
															aria-hidden="true"
															stroke="#292D32"
															strokeWidth={"0.1vh"}
														/>
													</button>
													<input
														type="phone"
														name="homePhone"
														placeholder="7734567890"
														value={profile.homePhone.number || ""}
														onChange={(e) => {
															setProfile({
																...profile,
																homePhone: {
																	...profile.homePhone,
																	number: e.target.value
																}
															});

															setError({
																...error,
																[e.target.name]: ""
															});
														}}
														onBlur={validatePhoneNumber}
														required
													/>
												</div>

												{/* Country Code Dropdown */}
												<div className={"relative"}>
													<Transition show={isHomePhoneCountryCodeDropdownOpen}>
														<div
															className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                                origin-top-right rounded-md shadow-2xl 
                                                                border border-gray-700 overflow-y-scroll
                                                                data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                data-[enter]:duration-100 data-[enter]:ease-out 
                                                                data-[leave]:duration-75 data-[leave]:ease-in`}
														>
															{
																countryCodes.map((country, index) => (
																	<button
																		key={index}
																		className="custom-phone-number-input-dropdown-button hover:bg-gray-300"
																		onMouseDown={() => setIsSelectingHomePhoneCountryCode(true)}
																		onClick={() => handleHomePhoneCountryCodeSelect(country)}
																	>
																		<img
																			src={country.countryFlagSrc}
																			alt={country.countryName}
																		/>
																		<p
																			className={"text-left"}
																			aria-selected={"false"}
																		>
																			{country.countryName}
																		</p>
																		<p
																			aria-selected={"false"}
																		>
																			{'(' + country.countryDialingCode + ')'}
																		</p>
																	</button>
																))
															}
														</div>
													</Transition>
												</div>
											</div>
											{
												error['homePhone'] &&
												<p className="custom-input-error-message"
												   id="error">
													{error['homePhone']}
												</p>
											}
										</div>

										{/* Office Phone Number */}
										<div className={"custom-phone-number-input"}>
											<label
												className=""
												htmlFor="officePhone"
											>
												{t("PROFILE_FORM.OFFICE_PHONE_NUMBER.LABEL")}
											</label>
											<div className="w-full h-full flex flex-col">
												<div
													className="custom-phone-number-input-container"
												>
													<button
														onBlur={() => {
															if (!isSelectingOfficePhoneCountryCode) {
																setIsOfficePhoneCountryCodeDropdownOpen(false);
															}
														}}
														onClick={() => setIsOfficePhoneCountryCodeDropdownOpen(!isOfficePhoneCountryCodeDropdownOpen)}
													>
														<img
															src={`/assets/country_flags/${profile.officePhone.charCode}.png`}
															alt={profile.officePhone.countryName}
														/>
														<p
															aria-selected={"false"}
														>
															{profile.officePhone.dialingCode}
														</p>
														<ChevronDownIcon
															// onClick={() => setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen)}
															className={`${isOfficePhoneCountryCodeDropdownOpen ? 'rotate-180' : 'rotate-0'} `}
															aria-hidden="true"
															stroke="#292D32"
															strokeWidth={"0.1vh"}
														/>
													</button>
													<input
														type={"number"}
														name="officePhone"
														placeholder="7734567890"
														value={profile.officePhone.number || ""}
														onChange={(e) => {
															setProfile({
																...profile,
																officePhone: {
																	...profile.officePhone,
																	number: e.target.value
																}
															});

															setError({
																...error,
																[e.target.name]: ""
															});
														}}
														onBlur={validatePhoneNumber}
														required
													/>
												</div>

												{/* Country Code Dropdown */}
												<div className={"relative"}>
													<Transition show={isOfficePhoneCountryCodeDropdownOpen}>
														<div
															className={`bg-white absolute left-0 right-0 z-[100] max-h-[15vh]
                                                                origin-top-right rounded-md shadow-2xl 
                                                                border border-gray-700 overflow-y-scroll
                                                                data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 
                                                                data-[enter]:duration-100 data-[enter]:ease-out 
                                                                data-[leave]:duration-75 data-[leave]:ease-in`}
														>
															{
																countryCodes.map((country, index) => (
																	<button
																		key={index}
																		className="custom-phone-number-input-dropdown-button hover:bg-gray-300"
																		onMouseDown={() => setIsSelectingOfficePhoneCountryCode(true)}
																		onClick={() => handleOfficePhoneCountryCodeSelect(country)}
																	>
																		<img
																			src={country.countryFlagSrc}
																			alt={country.countryName}
																		/>
																		<p
																			className={"text-left"}
																			aria-selected={"false"}
																		>
																			{country.countryName}
																		</p>
																		<p
																			aria-selected={"false"}
																		>
																			{'(' + country.countryDialingCode + ')'}
																		</p>
																	</button>
																))
															}
														</div>
													</Transition>
												</div>
											</div>
											{
												error['officePhone'] &&
												<p className="custom-input-error-message"
												   id="error">
													{error['officePhone']}
												</p>
											}
										</div>
									</div>
								</fieldset>
							</>
						)
					}
				</div>

				{/* Footer */}
				<div className="form-modal-footer-container">
					{/* Divider */}
					<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full"}/>

					{/* Submit Button Container */}
					<div className="flex justify-end">
						{/* Submit Button */}
						<button
							disabled={isSaveButtonDisabled}
							onClick={handleSubmit}
							className="form-modal-footer-container-submit-button"
						>
							<p>
								{t("PROFILE_FORM.SAVE")}
							</p>
							<svg
								viewBox="0 0 6 6"
								fill="none"
								id={"arrow-icon"}
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M3 1L5 3L3 5"
									id={"arrow-icon-tip"}
									stroke="black"
									strokeLinecap="square"
								/>
								<path
									d="M1 3L4 3"
									id={"arrow-icon-line"}
									stroke="black"
									strokeLinecap="square"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</Modal>
	);
}
